/**
 * VoiceInput - Composant de saisie vocale
 * Utilise l'API Web Speech Recognition pour convertir la voix en texte
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

export function VoiceInput({ 
  onTranscript, 
  language = 'fr-FR',
  className,
  disabled = false,
  size = 'icon'
}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [recognition, setRecognition] = useState(null);

  // Vérifier le support de l'API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = language;
      
      recognitionInstance.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript && onTranscript) {
          onTranscript(finalTranscript);
        }
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          toast.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
        } else if (event.error === 'no-speech') {
          toast.info('Aucune parole détectée. Réessayez.');
        } else {
          toast.error(`Erreur de reconnaissance vocale: ${event.error}`);
        }
      };
      
      recognitionInstance.onend = () => {
        setIsListening(false);
      };
      
      setRecognition(recognitionInstance);
    }
    
    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, [language]);

  const toggleListening = useCallback(() => {
    if (!recognition) return;
    
    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      try {
        recognition.start();
        setIsListening(true);
        toast.info('Parlez maintenant...', { duration: 2000 });
      } catch (err) {
        console.error('Error starting recognition:', err);
        // Si déjà en cours, on arrête et on redémarre
        recognition.stop();
        setTimeout(() => {
          try {
            recognition.start();
            setIsListening(true);
          } catch (e) {
            console.error('Failed to restart recognition:', e);
          }
        }, 100);
      }
    }
  }, [recognition, isListening]);

  if (!isSupported) {
    return null; // Ne rien afficher si non supporté
  }

  return (
    <Button
      type="button"
      variant={isListening ? "destructive" : "outline"}
      size={size}
      onClick={toggleListening}
      disabled={disabled}
      className={cn(
        "transition-all",
        isListening && "animate-pulse ring-2 ring-red-500 ring-offset-2 ring-offset-zinc-900",
        className
      )}
      title={isListening ? "Arrêter l'écoute" : "Saisie vocale"}
    >
      {isListening ? (
        <MicOff className="w-4 h-4" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </Button>
  );
}

export default VoiceInput;
